/**
 * SettingsGate
 * ------------
 * Shared password gate for the Settings tile and any tools the admin
 * promotes "into Settings" (e.g. Supervisor Schedules, Cleaning Questions,
 * Storage). One unlock per browser tab — backed by sessionStorage — so
 * navigating between these pages does not re-prompt.
 *
 * Usage:
 *   <SettingsGate><MyAdminPage /></SettingsGate>
 *
 * The component also exposes the constants and a helper `lockSettings()`
 * for the Lock button rendered on SettingsPage.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Lock, KeyRound } from "lucide-react";

export const SETTINGS_GATE_PASSWORD = "itcot1979";
export const SETTINGS_GATE_KEY = "tnpm_settings_unlocked";

export const SETTINGS_PATHS = [
  "/admin/settings",
  "/admin/supervisor-incharge",
  "/admin/storage",
  "/admin/supervisor-schedules",
  "/admin/cleaning-questions",
  "/admin/deploy-snapshot",
  "/admin/parks",
  "/admin/park-assets",
  "/admin/supervisors",
  "/admin/contractors",
  "/admin/employees",
  "/admin/holidays",
  "/admin/report-groups",
  "/admin/payroll-rates",
];

export function isSettingsPath(pathname) {
  if (!pathname) return false;
  return SETTINGS_PATHS.some((p) => pathname.startsWith(p));
}

export function isSettingsUnlocked() {
  try {
    return sessionStorage.getItem(SETTINGS_GATE_KEY) === "true";
  } catch {
    return false;
  }
}

export function unlockSettings() {
  try {
    sessionStorage.setItem(SETTINGS_GATE_KEY, "true");
  } catch {}
}

export function lockSettings() {
  try {
    sessionStorage.removeItem(SETTINGS_GATE_KEY);
  } catch {}
}

export function SettingsGate({ children, title = "Settings", subtitle = "Restricted area" }) {
  const location = useLocation();
  const [unlocked, setUnlocked] = useState(() => isSettingsUnlocked());
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setUnlocked(isSettingsUnlocked());
  }, [location.pathname]);

  if (unlocked) return children;

  const submit = (e) => {
    e?.preventDefault?.();
    if (pwd === SETTINGS_GATE_PASSWORD) {
      unlockSettings();
      setUnlocked(true);
      setError("");
    } else {
      setError("Incorrect password");
    }
  };

  return (
    <AppShell title={title} subtitle={subtitle}>
      <Card className="max-w-md">
        <CardContent className="flex flex-col gap-3 p-6">
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            <div>
              <div className="font-display text-base font-semibold">
                Settings is password protected
              </div>
              <div className="text-xs text-muted-foreground">
                Enter the settings password to continue.
              </div>
            </div>
          </div>
          <form onSubmit={submit} className="mt-2 flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Settings password</Label>
              <PasswordInput
                value={pwd}
                onChange={(e) => {
                  setPwd(e.target.value);
                  setError("");
                }}
                autoFocus
                data-testid="settings-gate-input"
                testIdToggle="settings-gate-toggle"
              />
              {error ? <div className="text-xs text-red-600">{error}</div> : null}
            </div>
            <Button type="submit" data-testid="settings-gate-submit">
              <KeyRound className="mr-2 h-4 w-4" />
              Unlock
            </Button>
          </form>
        </CardContent>
      </Card>
    </AppShell>
  );
}

export default SettingsGate;
